import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ReportVisual } from '../../services/reportsApi';
import ReportEmptyState from './ReportEmptyState';

type ReportManifestStatusProps = {
  visual: ReportVisual;
  compact?: boolean;
};

function chipColor(status: unknown): 'success' | 'warning' | 'error' | 'default' | 'info' {
  const normalized = String(status || '').trim().toLowerCase();
  if (['present', 'available', 'ready', 'complete', 'completed', 'ok', 'healthy'].includes(normalized)) return 'success';
  if (['warning', 'pending', 'partial', 'review'].includes(normalized)) return 'warning';
  if (['missing', 'blocked', 'failed', 'error', 'unavailable'].includes(normalized)) return 'error';
  if (['info', 'unknown'].includes(normalized)) return 'info';
  return 'default';
}

const ReportManifestStatus: React.FC<ReportManifestStatusProps> = ({ visual, compact = false }) => {
  const items = Array.isArray(visual.data?.items)
    ? visual.data.items
    : Array.isArray(visual.data?.rows)
      ? visual.data.rows
      : [];

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, height: '100%' }}>
      <Typography variant="h6">{visual.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1 : 1.5 }}>
        {visual.insight}
      </Typography>

      {visual.empty_state ? (
        <ReportEmptyState
          title={visual.empty_state.title}
          detail={visual.empty_state.detail}
          missingData={visual.empty_state.missing_data}
          compact={compact}
        />
      ) : (
        <Stack spacing={compact ? 0.75 : 1}>
          {items.map((item: Record<string, unknown>, index: number) => (
            <Paper key={String(item?.id || item?.row_id || index)} variant="outlined" sx={{ p: compact ? 1 : 1.25 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {String(item?.label || 'Item')}
                  </Typography>
                  {item?.detail ? (
                    <Typography variant="caption" color="text.secondary">
                      {String(item.detail)}
                    </Typography>
                  ) : null}
                </Box>
                <Chip
                  size="small"
                  variant="outlined"
                  label={String(item?.status || 'unknown')}
                  color={chipColor(item?.status)}
                />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default ReportManifestStatus;
